import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { isTokenExpired } from '@/utils/jwt';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: {
    id: number;
    email: string;
    fullName: string;
    roles: string[];
  } | null;
  setAuth: (token: string, refreshToken: string | null, user: AuthState['user']) => void;
  clearAuth: () => void;
  isAuthenticated: () => boolean;
  isRoot: () => boolean;
  isAdmin: () => boolean;
  isSuperRoot: () => boolean;
  setRefreshToken: (refreshToken: string | null) => void;
  updateToken: (token: string) => void;
}

/**
 * Secure storage helper with error handling
 */
const secureStorage = {
  setItem: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to store ${key}:`, error);
      throw error;
    }
  },
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to retrieve ${key}:`, error);
      return null;
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
    }
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => {
      // Restore tokens from localStorage on initialization
      const restoreTokens = () => {
        const storedToken = secureStorage.getItem('auth_token');
        const storedRefreshToken = secureStorage.getItem('refresh_token');
        
        if (storedToken || storedRefreshToken) {
          return {
            token: storedToken,
            refreshToken: storedRefreshToken,
          };
        }
        return { token: null, refreshToken: null };
      };

      // Initialize with tokens from localStorage
      const { token: initialToken, refreshToken: initialRefreshToken } = restoreTokens();

      return {
        token: initialToken,
        refreshToken: initialRefreshToken,
        user: null,
        setAuth: (token, refreshToken, user) => {
          // Validate token before storing
          if (token && isTokenExpired(token)) {
            console.warn('Attempting to store expired token');
          }

          secureStorage.setItem('auth_token', token);
          if (refreshToken) {
            secureStorage.setItem('refresh_token', refreshToken);
          }
          set({ token, refreshToken, user });
        },
        clearAuth: () => {
          secureStorage.removeItem('auth_token');
          secureStorage.removeItem('refresh_token');
          set({ token: null, refreshToken: null, user: null });
        },
        isAuthenticated: () => {
          // First check store, then fallback to localStorage
          let token = get().token;
          const user = get().user;
          
          // If token is not in store but exists in localStorage, restore it
          if (!token) {
            token = secureStorage.getItem('auth_token');
            if (token) {
              set({ token });
            }
          }
          
          // Check if token exists and is not expired
          if (!token || !user) {
            return false;
          }

          // Additional check: verify token is not expired
          if (isTokenExpired(token)) {
            return false;
          }

          return true;
        },
        isRoot: () => {
          const roles = get().user?.roles ?? [];
          return roles.includes('ROLE_ROOT') || roles.includes('ROLE_SUPER_ROOT');
        },
        isAdmin: () => {
          const roles = get().user?.roles ?? [];
          return roles.includes('ROLE_ADMIN') || roles.includes('ROLE_ROOT') || roles.includes('ROLE_SUPER_ROOT');
        },
        isSuperRoot: () => {
          return get().user?.roles.includes('ROLE_SUPER_ROOT') ?? false;
        },
        setRefreshToken: (refreshToken) => {
          if (refreshToken) {
            secureStorage.setItem('refresh_token', refreshToken);
          } else {
            secureStorage.removeItem('refresh_token');
          }
          set({ refreshToken });
        },
        updateToken: (token: string) => {
          // Validate token before updating
          if (isTokenExpired(token)) {
            console.warn('Attempting to update with expired token');
            return;
          }

          secureStorage.setItem('auth_token', token);
          set({ token });
        },
      };
    },
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      // Only persist user data, not tokens (tokens are stored separately for security)
      partialize: (state) => ({
        user: state.user,
      }),
      // Restore tokens from localStorage on rehydration
      onRehydrateStorage: () => {
        return (state) => {
          if (state) {
            const storedToken = secureStorage.getItem('auth_token');
            const storedRefreshToken = secureStorage.getItem('refresh_token');
            
            if (storedToken || storedRefreshToken) {
              state.token = storedToken;
              state.refreshToken = storedRefreshToken;
            }
          }
        };
      },
    }
  )
);
